import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { WALLET_MESSAGES, walletApi, walletErrorMessage } from "./walletApi";
import { formatDate, formatMoney } from "./walletFormat";

// Artist Studio > Music Wallet > Monthly statements (FSD: statement of earnings per month). One row per month with any money movement,
// newest first. Open a month for the full account statement: balance at the start, every kind of earning (streaming royalties,
// licence sales with the price, platform commission and revenue split, tips, memberships), refunds and reversals, adjustments, payouts
// and the balance at the end. The same statement downloads as a CSV file. Amounts are integers in minor units from the API.

const SIGNED = { signed: true };

function Row({ label, value, currency, strong, testId }) {
  return (
    <div className={`flex justify-between gap-4 py-1.5 ${strong ? "font-bold text-gray-900" : "text-gray-700"}`} data-testid={testId}>
      <span>{label}</span>
      <span>{formatMoney(value, currency, SIGNED)}</span>
    </div>
  );
}

function Section({ section, currency }) {
  const priced = section.gross_minor !== undefined;
  return (
    <div className="mt-6" data-testid={`section-${section.key}`}>
      <div className="flex items-baseline justify-between gap-4 border-b border-white pb-2">
        <h3 className="text-[16px] font-bold text-gray-900">{section.label}</h3>
        <span className="text-[16px] font-bold text-gray-900">{formatMoney(section.total_minor, currency, SIGNED)}</span>
      </div>
      {priced && section.count > 0 && (
        <p className="mt-2 text-sm text-gray-600" data-testid={`totals-${section.key}`}>
          Price {formatMoney(section.gross_minor, currency)}
          {" · "}Platform commission {formatMoney(section.commission_minor, currency)}
          {section.split_minor !== 0 && <>{" · "}Revenue split {formatMoney(section.split_minor, currency)}</>}
        </p>
      )}
      {section.count === 0 ? (
        <p className="mt-2 text-sm text-gray-500">Nothing this month.</p>
      ) : (
        <ul className="mt-1">
          {section.lines.map((l) => (
            <li key={l.id} className="flex flex-wrap items-baseline justify-between gap-x-4 border-b border-white/70 py-2 text-[14px]" data-testid="statement-line">
              <span className="min-w-0 flex-1 text-gray-900">
                <span className="mr-2 text-gray-500">{formatDate(l.date)}</span>
                {l.description}
                {l.reference && <span className="ml-2 text-xs text-gray-500">Ref {l.reference}</span>}
                {priced && l.gross_minor !== null && (
                  <span className="block text-xs text-gray-500">
                    Price {formatMoney(l.gross_minor, currency)} · Commission {formatMoney(l.commission_minor, currency)}
                    {l.split_minor !== 0 && <> · Split {formatMoney(l.split_minor, currency)}</>}
                  </span>
                )}
              </span>
              <span className="font-semibold text-gray-900">{formatMoney(l.amount_minor, currency, SIGNED)}</span>
            </li>
          ))}
        </ul>
      )}
      {section.truncated && <p className="mt-2 text-xs text-gray-500" data-testid="truncated-note">Only the first lines are shown here. Download the statement to see every line.</p>}
    </div>
  );
}

function download(file, name) {
  const url = window.URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

function MonthDetail({ month }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    let live = true;
    walletApi.statement(month).then(
      (r) => { if (live) setData(r); },
      (e) => { if (live) setError(walletErrorMessage(e, WALLET_MESSAGES.statement)); }
    );
    return () => { live = false; };
  }, [month]);

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      download(await walletApi.downloadStatement(month), `statement-${month}.csv`);
    } catch (e) {
      setSaveError(WALLET_MESSAGES.statementDownload);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <p className="px-6 pb-5 text-sm text-red-700" role="alert" data-testid="statement-error">{error}</p>;
  if (!data) return <div className="flex justify-center pb-5"><Loading /></div>;
  const s = data.summary;
  const c = data.currency;
  return (
    <div className="px-6 pb-6" data-testid="statement-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {formatDate(data.period_start)} to {formatDate(new Date(new Date(data.period_end).getTime() - 1))}
          {data.status === "open" && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800" data-testid="statement-open">Month still open</span>}
        </p>
        <button type="button" onClick={save} disabled={saving} className="rounded-full border px-4 py-1.5 text-sm font-semibold disabled:opacity-60" style={{ color: ACCENT, borderColor: ACCENT }} data-testid="statement-download">
          {saving ? "Preparing…" : "Download CSV"}
        </button>
      </div>
      {saveError && <p className="mt-2 text-sm text-red-700" role="alert" data-testid="statement-download-error">{saveError}</p>}

      <div className="mt-4 rounded-xl bg-white/70 px-4 py-2 text-[14px]" data-testid="statement-summary">
        <Row label="Balance at the start of the month" value={s.opening_balance_minor} currency={c} testId="sum-opening" />
        <Row label="Earnings" value={s.earnings_minor} currency={c} testId="sum-earnings" />
        <Row label="Refunds and reversals" value={s.refunds_minor} currency={c} testId="sum-refunds" />
        {s.adjustments_minor !== 0 && <Row label="Adjustments" value={s.adjustments_minor} currency={c} testId="sum-adjustments" />}
        <Row label="Payouts" value={s.payouts_minor} currency={c} testId="sum-payouts" />
        <div className="border-t border-gray-200 mt-1">
          <Row label="Balance at the end of the month" value={s.closing_balance_minor} currency={c} strong testId="sum-closing" />
        </div>
        <p className="pb-1 text-xs text-gray-500">{data.note}</p>
      </div>

      {data.sections.map((section) => <Section key={section.key} section={section} currency={c} />)}
    </div>
  );
}

function StatementsBody() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const latest = useRef(0);

  const load = useCallback(() => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    setError("");
    setRows(null);
    walletApi.statements({ limit: 24 }).then(
      (r) => { if (ticket === latest.current) setRows(r?.statements || []); },
      (e) => {
        if (ticket !== latest.current) return;
        setError(walletErrorMessage(e, WALLET_MESSAGES.statements));
        setRows([]);
      }
    );
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="monthly-statements">
      <Link to="/artist-studio/wallet" className="text-sm font-semibold text-gray-700 hover:underline">← Back to Music Wallet</Link>
      <h1 className="mt-4 text-[32px] font-bold leading-tight text-gray-900">Monthly statements</h1>
      <p className="mt-1 text-[15px] text-gray-600">
        Everything that moved in your wallet each month. For the streams behind your royalties, see{" "}
        <Link to="/artist-studio/wallet/statements" className="font-semibold underline" data-testid="streaming-link">Royalty statements</Link>.
      </p>

      {error && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="statements-error">
          {error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button>
        </p>
      )}

      <div className="mt-5 rounded-2xl bg-[#f1f6f2]">
        {rows === null ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : rows.length === 0 ? (
          !error && <p className="p-8 text-center text-gray-500" data-testid="statements-empty">No statements yet. A month appears here as soon as money moves in your wallet.</p>
        ) : (
          rows.map((s) => (
            <div key={s.month} className="border-t border-white/70 first:border-t-0" data-testid="statement-row">
              <button
                type="button"
                className="flex w-full flex-wrap items-center justify-between gap-3 px-6 py-4 text-left"
                aria-expanded={open === s.month}
                onClick={() => setOpen(open === s.month ? null : s.month)}
              >
                <span className="text-[16px] font-semibold text-gray-900">
                  {s.label}
                  {s.status === "open" && <span className="ml-2 text-xs font-semibold text-amber-800">Open</span>}
                </span>
                <span className="text-sm text-gray-600">Earned {formatMoney(s.earnings_minor, s.currency)}{s.refunds_minor !== 0 && <> · Refunded {formatMoney(s.refunds_minor, s.currency)}</>}{s.payouts_minor !== 0 && <> · Paid out {formatMoney(s.payouts_minor, s.currency)}</>}</span>
                <span className="text-[16px] font-bold" style={{ color: ACCENT }}>{formatMoney(s.closing_balance_minor, s.currency)}</span>
              </button>
              {open === s.month && <MonthDetail month={s.month} />}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function MonthlyStatements() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="wallet" heading="" tabs={[]} maxWidth="max-w-4xl">
      <StatementsBody />
    </StudioGate>
  );
}
